import { Menu, X, User, Bell, Settings, ChevronRight, LayoutDashboard, Wallet, Send, BarChart3, Users, FileText } from 'lucide-react';
import { useState } from 'react';
import { BUILD_INFO } from '@/buildInfo';
import { useTenant } from './MockTenantProvider';

/**
 * HarnessCWLayout - CloudWaitress-style layout for UI Harness
 * 
 * IMPORTANT: This is ONLY for /__ui-harness/cw routes
 * Identical to CWLayout but without Clerk dependencies
 * 
 * DOES NOT:
 * - Use Clerk components
 * - Make API calls
 * - Handle authentication
 */
export default function HarnessCWLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { tenant } = useTenant() || {};

  const formatBuildTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <div className="cw-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="cw-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`cw-sidebar ${sidebarOpen ? 'cw-sidebar--open' : ''}`}>
        {/* Logo Area */}
        <div className="cw-sidebar-header">
          <div className="cw-logo-placeholder">
            <div className="cw-logo-icon">CW</div>
          </div>
          <button 
            className="cw-sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Placeholder */}
        <nav className="cw-nav">
          <div className="cw-nav-section">
            <div className="cw-nav-label">MAIN</div>
            <div className="cw-nav-item cw-nav-item--active">
              <LayoutDashboard className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Dashboard</span>
              <ChevronRight size={16} className="cw-nav-chevron" />
            </div>
            <div className="cw-nav-item">
              <Wallet className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Wallet</span>
            </div>
            <div className="cw-nav-item">
              <Send className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Campaigns</span>
            </div>
            <div className="cw-nav-item">
              <BarChart3 className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Analytics</span>
            </div>
          </div>

          <div className="cw-nav-section">
            <div className="cw-nav-label">MANAGE</div>
            <div className="cw-nav-item">
              <Users className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Customers</span>
            </div>
            <div className="cw-nav-item">
              <FileText className="cw-nav-icon" size={18} />
              <span className="cw-nav-text">Templates</span>
            </div>
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="cw-sidebar-footer">
          <div className="cw-version">v{BUILD_INFO.version}</div>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="cw-main-wrapper">
        {/* Top Bar */}
        <header className="cw-topbar">
          <div className="cw-topbar-left">
            <button 
              className="cw-menu-btn"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="cw-page-title">
              {tenant?.business?.name || 'Dashboard'}
            </div>
          </div>

          <div className="cw-topbar-right">
            <button className="cw-icon-btn">
              <Bell size={18} />
            </button>
            <button className="cw-icon-btn">
              <Settings size={18} />
            </button>
            <div className="cw-user-menu">
              <div className="cw-user-avatar">
                <User size={16} />
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="cw-content">
          <div className="cw-content-inner">
            {children}
          </div>
        </main>
      </div>

      {/* Inline Styles (CW-specific) */}
      <style>{`
        /* Layout Container */
        .cw-layout {
          display: flex;
          min-height: 100vh;
          background: var(--cw-surface-bg);
          font-family: var(--cw-font-family);
        }

        /* Overlay for mobile */
        .cw-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: var(--cw-z-overlay);
        }

        /* Sidebar */
        .cw-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          width: var(--cw-sidebar-width);
          background: var(--cw-sidebar-bg);
          border-right: 1px solid var(--cw-border);
          box-shadow: 1px 0 3px rgba(0, 0, 0, 0.04);
          display: flex;
          flex-direction: column;
          z-index: var(--cw-z-sidebar);
          transition: transform var(--cw-transition-slow);
        }

        @media (max-width: 1023px) {
          .cw-sidebar {
            transform: translateX(-100%);
          }
          .cw-sidebar--open {
            transform: translateX(0);
          }
        }

        @media (min-width: 1024px) {
          .cw-sidebar {
            transform: translateX(0);
          }
        }

        /* Sidebar Header */
        .cw-sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--cw-spacing-md);
          border-bottom: 1px solid var(--cw-border-light);
        }

        .cw-logo-placeholder {
          display: flex;
          align-items: center;
        }

        .cw-logo-icon {
          width: var(--cw-logo-size);
          height: var(--cw-logo-size);
          background: var(--cw-primary);
          border-radius: var(--cw-radius);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--cw-font-weight-bold);
          font-size: var(--cw-font-size-base);
          color: var(--cw-text-white);
        }

        .cw-sidebar-close {
          display: none;
          background: none;
          border: none;
          color: var(--cw-text-secondary);
          cursor: pointer;
          padding: 4px;
        }

        @media (max-width: 1023px) {
          .cw-sidebar-close {
            display: block;
          }
        }

        /* Navigation */
        .cw-nav {
          flex: 1;
          overflow-y: auto;
          padding: var(--cw-spacing-md) 0;
        }

        .cw-nav-section {
          margin-bottom: var(--cw-spacing-xl);
        }

        .cw-nav-label {
          font-size: var(--cw-font-size-xs);
          font-weight: var(--cw-font-weight-bold);
          letter-spacing: var(--cw-letter-spacing);
          color: var(--cw-text-sidebar-label);
          padding: var(--cw-spacing-xs) var(--cw-spacing-md);
          text-transform: uppercase;
        }

        .cw-nav-item {
          display: flex;
          align-items: center;
          gap: var(--cw-spacing-sm);
          padding: 9px var(--cw-spacing-md);
          color: var(--cw-text-sidebar);
          cursor: pointer;
          transition: all var(--cw-transition);
          position: relative;
          margin: 1px 0;
        }

        .cw-nav-item:hover {
          background: var(--cw-nav-item-hover);
        }

        .cw-nav-item--active {
          background: var(--cw-nav-item-active-bg);
          color: var(--cw-text-sidebar);
          border-left: var(--cw-nav-item-active-border) solid var(--cw-primary);
        }

        .cw-nav-icon {
          flex-shrink: 0;
          color: var(--cw-text-muted);
        }

        .cw-nav-text {
          flex: 1;
          font-size: var(--cw-font-size-sm);
          font-weight: var(--cw-font-weight-medium);
        }

        .cw-nav-chevron {
          opacity: 0;
          transition: opacity var(--cw-transition);
          color: var(--cw-primary);
        }

        .cw-nav-item--active .cw-nav-chevron {
          opacity: 1;
        }

        /* Sidebar Footer */
        .cw-sidebar-footer {
          padding: var(--cw-spacing-md);
          border-top: 1px solid var(--cw-border-light);
          text-align: center;
        }

        .cw-version {
          font-size: var(--cw-font-size-xs);
          color: var(--cw-text-muted);
        }

        /* Main Wrapper */
        .cw-main-wrapper {
          flex: 1;
          margin-left: 0;
          display: flex;
          flex-direction: column;
        }

        @media (min-width: 1024px) {
          .cw-main-wrapper {
            margin-left: var(--cw-sidebar-width);
          }
        }

        /* Top Bar */
        .cw-topbar {
          height: var(--cw-topbar-height);
          background: var(--cw-topbar-bg);
          border-bottom: 1px solid var(--cw-border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 var(--cw-spacing-lg);
          position: sticky;
          top: 0;
          z-index: var(--cw-z-topbar);
        }

        .cw-topbar-left {
          display: flex;
          align-items: center;
          gap: var(--cw-spacing-md);
        }

        .cw-menu-btn {
          background: none;
          border: none;
          color: var(--cw-text-secondary);
          cursor: pointer;
          padding: var(--cw-spacing-xs);
          border-radius: var(--cw-radius);
          display: flex;
          align-items: center;
        }

        .cw-menu-btn:hover {
          background: var(--cw-surface-bg);
        }

        @media (min-width: 1024px) {
          .cw-menu-btn {
            display: none;
          }
        }

        .cw-page-title {
          font-size: var(--cw-font-size-xl);
          font-weight: var(--cw-font-weight-semibold);
          color: var(--cw-text-primary);
        }

        .cw-topbar-right {
          display: flex;
          align-items: center;
          gap: var(--cw-spacing-xs);
        }

        .cw-icon-btn {
          background: none;
          border: none;
          color: var(--cw-text-muted);
          cursor: pointer;
          padding: var(--cw-spacing-xs);
          border-radius: var(--cw-radius);
          display: flex;
          align-items: center;
        }

        .cw-icon-btn:hover {
          background: var(--cw-surface-bg);
          color: var(--cw-text-secondary);
        }

        .cw-user-menu {
          margin-left: var(--cw-spacing-xs);
        }

        .cw-user-avatar {
          width: var(--cw-avatar-size);
          height: var(--cw-avatar-size);
          background: var(--cw-primary);
          border-radius: var(--cw-radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--cw-text-white);
          cursor: pointer;
        }

        /* Content Area */
        .cw-content {
          flex: 1;
          overflow-y: auto;
        }

        .cw-content-inner {
          padding: var(--cw-content-padding);
          max-width: var(--cw-content-max-width);
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
}
